/**
 * Custom hook for fetching and joining map data
 * - GeoJSON boundary files
 * - Geographic analysis data (enrollment by region/district)
 * - School GPS coordinates
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getGeographicAnalysis, getSchoolsGPSData, SchoolGPSData } from '../../services/analysisApi';
import {
  joinGeoData,
  classifyNaturalBreaks,
  getColorScale,
  GeoFeatureData,
  IndicatorType,
} from './mapUtils';

export type MapLevel = 'region' | 'district' | 'ward';

interface UseMapDataOptions {
  level: MapLevel;
  indicator: IndicatorType;
  year?: string;
  selectedRegionCode?: string;
  selectedDistrictCode?: string;
  showSchools: boolean;
}

interface MapDataResult {
  // GeoJSON
  geoJsonData: GeoJSON.FeatureCollection | null;
  loading: boolean;
  error: string | null;

  // Joined enrollment data
  dataMap: Map<string, GeoFeatureData>;
  breaks: number[];
  colors: string[];

  // School markers
  schoolsData: SchoolGPSData[];
  schoolsLoading: boolean;

  // Actions
  refetch: () => void;
}

const GEOJSON_BASE_PATH = '/geojson';

const GEOJSON_FILES: Record<MapLevel, string> = {
  region: `${GEOJSON_BASE_PATH}/zanzibar_regions.geojson`,
  district: `${GEOJSON_BASE_PATH}/zanzibar_districts.geojson`,
  ward: `${GEOJSON_BASE_PATH}/zanzibar_wards.geojson`,
};

export function useMapData(options: UseMapDataOptions): MapDataResult {
  const { level, indicator, year, selectedRegionCode, selectedDistrictCode, showSchools } = options;

  // State
  const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [enrollmentData, setEnrollmentData] = useState<any[]>([]);
  const [schoolsData, setSchoolsData] = useState<SchoolGPSData[]>([]);
  const [loading, setLoading] = useState(true);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only use selectedDistrictCode for GeoJSON fetching at ward level
  // This prevents unnecessary re-fetches when clicking districts at district level
  const wardDistrictCode = level === 'ward' ? selectedDistrictCode : undefined;

  // Fetch GeoJSON boundary file
  const fetchGeoJSON = useCallback(async () => {
    const filePath = GEOJSON_FILES[level];
    if (!filePath) return null;

    try {
      const response = await fetch(filePath);
      if (!response.ok) throw new Error(`Failed to load ${filePath}`);
      const data = await response.json();

      // If drilling into a region, filter district features by region code
      if (level === 'district' && selectedRegionCode) {
        data.features = data.features.filter(
          (f: GeoJSON.Feature) => String(f.properties?.reg_code) === String(selectedRegionCode)
        );
      }

      // If drilling into a district, filter ward features by BOTH reg_code + dist_code
      // (dist_code is NOT unique across regions — e.g., dist_code=01 exists in all 5 regions)
      if (level === 'ward' && wardDistrictCode && selectedRegionCode) {
        const distCode = String(wardDistrictCode);
        const regCode = String(selectedRegionCode);
        data.features = data.features.filter(
          (f: GeoJSON.Feature) =>
            String(f.properties?.dist_code) === distCode &&
            String(f.properties?.reg_code) === regCode
        );
      }

      return data;
    } catch (err: any) {
      console.error('Error loading GeoJSON:', err);
      throw err;
    }
  }, [level, selectedRegionCode, wardDistrictCode]);

  // Fetch enrollment data from geographic-analysis API
  const fetchEnrollmentData = useCallback(async () => {
    try {
      // API only supports region/district/school - map ward to district
      const apiLevel = level === 'ward' ? 'district' : level;
      const result = await getGeographicAnalysis(apiLevel as 'region' | 'district' | 'school', 'enrollment', year);
      return result?.data || [];
    } catch (err: any) {
      console.error('Error fetching enrollment data:', err);
      return [];
    }
  }, [level, year]);

  // Fetch school GPS data
  const fetchSchoolsGPS = useCallback(async () => {
    if (!showSchools) return [];

    setSchoolsLoading(true);
    try {
      const data = await getSchoolsGPSData(selectedRegionCode, selectedDistrictCode);
      return data;
    } catch (err: any) {
      console.error('Error fetching GPS data:', err);
      return [];
    } finally {
      setSchoolsLoading(false);
    }
  }, [showSchools, selectedRegionCode, selectedDistrictCode]);

  // Main data fetch
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [geoData, enrollment] = await Promise.all([
        fetchGeoJSON(),
        fetchEnrollmentData(),
      ]);

      if (geoData) {
        setGeoJsonData(geoData);
      }
      setEnrollmentData(enrollment);
    } catch (err: any) {
      setError(err.message || 'Failed to load map data');
    } finally {
      setLoading(false);
    }
  }, [fetchGeoJSON, fetchEnrollmentData]);

  // Fetch schools separately (can be toggled)
  useEffect(() => {
    if (showSchools) {
      fetchSchoolsGPS().then(setSchoolsData);
    } else {
      setSchoolsData([]);
    }
  }, [showSchools, fetchSchoolsGPS]);

  // Initial fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Join GeoJSON + enrollment data
  const dataMap = useMemo(() => {
    if (!geoJsonData || enrollmentData.length === 0) {
      return new Map<string, GeoFeatureData>();
    }
    return joinGeoData(geoJsonData.features, enrollmentData, level);
  }, [geoJsonData, enrollmentData, level]);

  // Calculate classification breaks
  const breaks = useMemo(() => {
    const values: number[] = [];
    dataMap.forEach((data) => {
      let val: number | undefined;
      switch (indicator) {
        case 'enrollment':
          val = data.total_students;
          break;
        case 'schools':
          val = data.total_schools;
          break;
        case 'gender_parity':
          val = data.gender_parity;
          break;
        default:
          val = data.total_students;
      }
      if (val !== undefined && val > 0) values.push(val);
    });

    return classifyNaturalBreaks(values, 5);
  }, [dataMap, indicator]);

  // Get color scale
  const colors = useMemo(() => getColorScale(indicator), [indicator]);

  return {
    geoJsonData,
    loading,
    error,
    dataMap,
    breaks,
    colors,
    schoolsData,
    schoolsLoading,
    refetch: fetchAllData,
  };
}
